import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GetRestaurants } from "../api/restaurant";
import { UsersForUpdate, UpdateUser } from "../api/users";
import { GetAccessPosts } from "../api/post";

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) {
    return payload.detail[0].msg;
  }
  return fallback;
}

function initials(user) {
  return `${(user?.surname?.[0] || "").toUpperCase()}${(user?.name?.[0] || "").toUpperCase()}`;
}

function fullName(user) {
  return `${user?.surname ?? ""} ${user?.name ?? ""}`.trim() || "Без имени";
}

const Users = ({ token }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [sync, setSync] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(0);
  const [dataInput, setDataInput] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [postsDict, setPostsDict] = useState({});
  const [restaurantsDict, setRestaurantsDict] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRestaurant, setUserRestaurant] = useState(0);
  const [userPost, setUserPost] = useState(0);
  const [userSync, setUserSync] = useState(false);
  const [userNewPassword, setUserNewPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setCatalogLoading(true);
      try {
        const data = await GetRestaurants(token, 1, 50);
        const posts = await GetAccessPosts(token);
        if (cancelled) return;
        const restList = data?.data || [];
        setRestaurants(restList);
        setAllPosts(posts || []);
        const nextPosts = {};
        for (const post of posts || []) nextPosts[post.id] = post;
        setPostsDict(nextPosts);
        const nextRestaurants = {};
        for (const rest of restList) nextRestaurants[rest.id] = rest;
        setRestaurantsDict(nextRestaurants);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const query = dataInput.trim();
    if (!query) {
      setAllUsers([]);
      setIsSearching(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await UsersForUpdate(
          token,
          selectedRestaurant,
          query,
          sync,
        );
        if (!cancelled) setAllUsers(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        if (!cancelled) setAllUsers([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, selectedRestaurant, dataInput, sync]);

  function selectUser(user) {
    setSelectedUser(user);
    setUserRestaurant(user.restaurant_id);
    setUserPost(user.post_id);
    setUserSync(Boolean(user.syncing_with_bk));
    setUserNewPassword("");
    setMessage(null);
    window.requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }

  const passwordOk = userNewPassword.length === 0 || userNewPassword.length >= 8;
  const isDirty = useMemo(() => {
    if (!selectedUser) return false;
    return (
      Number(userRestaurant) !== Number(selectedUser.restaurant_id) ||
      Number(userPost) !== Number(selectedUser.post_id) ||
      Boolean(userSync) !== Boolean(selectedUser.syncing_with_bk) ||
      userNewPassword.length > 0
    );
  }, [selectedUser, userRestaurant, userPost, userSync, userNewPassword]);

  async function updateUser(event) {
    event.preventDefault();
    if (!selectedUser || !isDirty || !passwordOk || isSubmitting) return;

    setIsSubmitting(true);
    setMessage(null);
    try {
      const payload = {
        id: selectedUser.id,
        post_id: Number(userPost),
        restaurant_id: Number(userRestaurant),
        syncing_with_bk: userSync,
      };
      if (userNewPassword) payload.hashed_password = userNewPassword;

      const data = await UpdateUser(token, payload);
      if (data?.detail && !Number.isFinite(data?.id)) {
        setMessage({
          ok: false,
          text: errorText(data, "Не удалось сохранить изменения"),
        });
        return;
      }

      setAllUsers((prev) =>
        prev.map((user) => (user.id === data.id ? { ...user, ...data } : user)),
      );
      setSelectedUser((prev) => ({ ...prev, ...data }));
      setUserNewPassword("");
      setMessage({ ok: true, text: "Изменения сохранены" });
    } catch (error) {
      console.error(error);
      setMessage({ ok: false, text: "Не удалось сохранить изменения" });
    } finally {
      setIsSubmitting(false);
    }
  }

  const queryStarted = dataInput.trim().length > 0;
  const restaurantName = (id) => restaurantsDict[id]?.name || "—";
  const postName = (id) => postsDict[id]?.name || "—";

  return (
    <div className="users-page">
      <div className="reports-header">
        <div>
          <h1>Сотрудники</h1>
          <p className="reports-hint">
            Найдите человека по фамилии или имени, затем измените ресторан,
            должность, синхронизацию или пароль
          </p>
        </div>
        <Link to="/profile" className="reports-back-link">
          ← К профилю
        </Link>
      </div>

      <div className="users-filters">
        <label className="users-field users-field--search">
          <span>Поиск</span>
          <input
            type="search"
            className="form-control"
            value={dataInput}
            onChange={(event) => setDataInput(event.target.value)}
            placeholder="Фамилия или имя"
            autoComplete="off"
          />
        </label>
        <label className="users-field">
          <span>Ресторан</span>
          <select
            className="form-select"
            value={selectedRestaurant}
            onChange={(event) => setSelectedRestaurant(event.target.value)}
            disabled={catalogLoading}
          >
            <option value={0}>Все рестораны</option>
            {restaurants.map((rest) => (
              <option value={rest.id} key={rest.id}>
                {rest.name}
              </option>
            ))}
          </select>
        </label>
        <label className="users-switch">
          <input
            type="checkbox"
            checked={sync}
            onChange={(event) => setSync(event.target.checked)}
          />
          <span className="users-switch-ui" aria-hidden="true" />
          <span className="users-switch-text">
            Только с синхронизацией БК
          </span>
        </label>
      </div>

      <div className="users-layout">
        <section className="users-list">
          <div className="users-list-head">
            <h2>Результаты</h2>
            <span className="users-count">
              {isSearching
                ? "Поиск…"
                : queryStarted
                  ? `${allUsers.length} чел.`
                  : "—"}
            </span>
          </div>

          {!queryStarted ? (
            <div className="users-empty">
              Начните вводить фамилию или имя — список появится здесь
            </div>
          ) : isSearching && allUsers.length === 0 ? (
            <div className="users-empty">Ищем сотрудников…</div>
          ) : allUsers.length === 0 ? (
            <div className="users-empty">
              Никого не нашли. Попробуйте другую фамилию или снимите фильтры
            </div>
          ) : (
            <div className="users-table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Сотрудник</th>
                    <th>Должность</th>
                    <th>Ресторан</th>
                    <th>БК</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((user) => {
                    const active = selectedUser?.id === user.id;
                    return (
                      <tr
                        key={user.id}
                        className={active ? "is-active" : ""}
                        onClick={() => selectUser(user)}
                      >
                        <td>
                          <div className="users-person">
                            <span className="users-avatar">{initials(user)}</span>
                            <div>
                              <strong>{fullName(user)}</strong>
                              <em>#{user.id}</em>
                            </div>
                          </div>
                        </td>
                        <td>{postName(user.post_id)}</td>
                        <td>{restaurantName(user.restaurant_id)}</td>
                        <td>
                          <span
                            className={`users-badge ${user.syncing_with_bk ? "is-ok" : "is-off"}`}
                          >
                            {user.syncing_with_bk ? "Да" : "Нет"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="users-editor" ref={editorRef}>
          {!selectedUser ? (
            <div className="users-editor-empty">
              <p>Выберите сотрудника</p>
              <span>Карточка редактирования откроется справа</span>
            </div>
          ) : (
            <form className="users-editor-card" onSubmit={updateUser}>
              <div className="users-editor-head">
                <span className="users-avatar users-avatar--lg">
                  {initials(selectedUser)}
                </span>
                <div>
                  <h3>{fullName(selectedUser)}</h3>
                  <p>
                    {postName(selectedUser.post_id)} · #{selectedUser.id}
                  </p>
                </div>
                <button
                  type="button"
                  className="users-editor-close"
                  onClick={() => {
                    setSelectedUser(null);
                    setMessage(null);
                  }}
                  aria-label="Закрыть карточку"
                >
                  ×
                </button>
              </div>

              {message ? (
                <div className={message.ok ? "login-success" : "login-error"}>
                  {message.text}
                </div>
              ) : null}

              <label className="users-field">
                <span>Ресторан</span>
                <select
                  className="form-select"
                  value={userRestaurant}
                  onChange={(event) => setUserRestaurant(event.target.value)}
                >
                  {restaurants.map((rest) => (
                    <option value={rest.id} key={rest.id}>
                      {rest.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="users-field">
                <span>Должность</span>
                <select
                  className="form-select"
                  value={userPost}
                  onChange={(event) => setUserPost(event.target.value)}
                >
                  {allPosts.map((post) => (
                    <option value={post.id} key={post.id}>
                      {post.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="users-field">
                <span>Новый пароль</span>
                <input
                  type="password"
                  className="form-control"
                  value={userNewPassword}
                  onChange={(event) => setUserNewPassword(event.target.value)}
                  autoComplete="new-password"
                  placeholder="Не менять"
                />
                {userNewPassword && !passwordOk ? (
                  <em className="users-field-hint is-bad">Не меньше 8 символов</em>
                ) : (
                  <em className="users-field-hint">Оставьте пустым, если пароль не меняете</em>
                )}
              </label>

              <label className="users-switch">
                <input
                  type="checkbox"
                  checked={userSync}
                  onChange={(event) => setUserSync(event.target.checked)}
                />
                <span className="users-switch-ui" aria-hidden="true" />
                <span className="users-switch-text">Синхронизация с БК</span>
              </label>

              <button
                type="submit"
                className="btn login-submit"
                disabled={!isDirty || !passwordOk || isSubmitting}
              >
                {isSubmitting ? "Сохранение…" : "Сохранить изменения"}
              </button>
            </form>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Users;
